import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, message, Space, Popconfirm, Card, Typography, Select, Input, Switch, Tag, Tabs, Descriptions, Drawer, Divider, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, PlayCircleOutlined, InfoCircleOutlined, RocketOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';
import { t } from '../utils/i18n';

const { Title, Text, Paragraph, Link } = Typography;
const { TextArea } = Input;


const Plugins = () => {
  const [builtInPlugins, setBuiltInPlugins] = useState([]);
  const [customPlugins, setCustomPlugins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [builtInLoading, setBuiltInLoading] = useState(false);
  const [customLoading, setCustomLoading] = useState(false);

  // 自定义插件搜索/筛选
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [languageFilter, setLanguageFilter] = useState(null);
  const [isPublicFilter, setIsPublicFilter] = useState(null);

  // 插件编辑弹窗状态
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingPlugin, setEditingPlugin] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    code: '',
    language: 'javascript',
    is_public: false,
  });
  const [saving, setSaving] = useState(false);

  // 插件详情抽屉
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [detailPlugin, setDetailPlugin] = useState(null);

  // 执行插件弹窗
  const [executeModalVisible, setExecuteModalVisible] = useState(false);
  const [executingPlugin, setExecutingPlugin] = useState(null);
  const [executeParams, setExecuteParams] = useState('{}');
  const [executeResult, setExecuteResult] = useState(null);
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    fetchBuiltInPlugins();
    fetchCustomPlugins();
  }, []);

  const fetchBuiltInPlugins = async () => {
    setBuiltInLoading(true);
    try {
      const response = await adminAPI.getBuiltInPlugins();
      if (response.code === 0 || response.plugins) {
        setBuiltInPlugins(response.plugins || response.data?.plugins || []);
      }
    } catch (error) {
      message.error(error.response?.data?.message || error.message || '获取内置插件失败');
    } finally {
      setBuiltInLoading(false);
    }
  };

  const fetchCustomPlugins = async () => {
    setCustomLoading(true);
    try {
      const response = await adminAPI.getCustomPlugins();
      if (response.code === 0 || response.plugins) {
        setCustomPlugins(response.plugins || response.data?.plugins || []);
      }
    } catch (error) {
      message.error(error.response?.data?.message || error.message || '获取自定义插件失败');
    } finally {
      setCustomLoading(false);
    }
  };

  const handleCreatePlugin = () => {
    setEditingPlugin(null);
    setEditFormData({
      name: '',
      display_name: '',
      description: '',
      code: getDefaultCode('javascript'),
      language: 'javascript',
      is_public: false,
    });
    setEditModalVisible(true);
  };

  const handleEditPlugin = (record) => {
    setEditingPlugin(record);
    setEditFormData({
      name: record.name || '',
      display_name: record.display_name || '',
      description: record.description || '',
      code: record.code || '',
      language: record.language || 'javascript',
      is_public: record.is_public || false,
    });
    setEditModalVisible(true);
  };

  const handleSavePlugin = async () => {
    if (!editFormData.name || !editFormData.code) {
      message.error(t('plugin_name') + ' & ' + t('plugin_code') + ' ' + t('required_field'));
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: editFormData.name,
        display_name: editFormData.display_name,
        description: editFormData.description,
        code: editFormData.code,
        language: editFormData.language,
        is_public: editFormData.is_public,
      };

      let response;
      if (editingPlugin) {
        response = await adminAPI.updateCustomPlugin(editingPlugin.id, data);
      } else {
        response = await adminAPI.createCustomPlugin(data);
      }

      if (response.code === 0) {
        message.success(editingPlugin ? t('plugin_updated') : t('plugin_created'));
        setEditModalVisible(false);
        fetchCustomPlugins();
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlugin = async (id) => {
    try {
      const response = await adminAPI.deleteCustomPlugin(id);
      if (response.code === 0) {
        message.success(t('plugin_deleted'));
        fetchCustomPlugins();
      } else {
        message.error(response.message || t('delete_failed'));
      }
    } catch (error) {
      message.error(error.response?.data?.message || t('delete_failed'));
    }
  };

  const handleLanguageChange = (lang) => {
    setEditFormData(prev => ({
      ...prev,
      language: lang,
      code: getDefaultCode(lang),
    }));
  };

  const getDefaultCode = (lang) => {
    switch (lang) {
      case 'javascript':
        return `// JavaScript Plugin Example
function process(params) {
  // Access input parameters
  const { input } = params;
  
  // Process the input
  const result = {
    output: \`Processed: \${input}\`,
    timestamp: new Date().toISOString(),
    success: true
  };
  
  return result;
}

// Export the processing function
module.exports = { process };`;
      case 'python':
        return `# Python Plugin Example
def process(params):
    # Access input parameters
    input_data = params.get('input', '')
    
    # Process the input
    result = {
        'output': f'Processed: {input_data}',
        'timestamp': datetime.now().isoformat(),
        'success': True
    }
    
    return result`;
      case 'lua':
        return `-- Lua Plugin Example
function process(params)
    -- Access input parameters
    local input_data = params.input or ''
    
    -- Process the input
    local result = {
        output = 'Processed: ' .. input_data,
        timestamp = os.date('!%Y-%m-%dT%H:%M:%SZ'),
        success = true
    }
    
    return result
end`;
      default:
        return '';
    }
  };

  const handleExecutePlugin = async () => {
    setExecuting(true);
    setExecuteResult(null);
    try {
      let params;
      try {
        params = JSON.parse(executeParams);
      } catch (e) {
        message.error('Invalid JSON parameters');
        setExecuting(false);
        return;
      }

      const response = await adminAPI.executeCustomPlugin(executingPlugin.id, params);
      if (response.code === 0) {
        setExecuteResult(response.result);
        message.success(t('execution_success'));
      } else {
        message.error(response.message || t('execution_failed'));
      }
    } catch (error) {
      message.error(error.response?.data?.message || t('execution_failed'));
    } finally {
      setExecuting(false);
    }
  };

  const openDetailDrawer = (plugin, isBuiltIn) => {
    setDetailPlugin({ ...plugin, is_built_in: isBuiltIn });
    setDetailDrawerVisible(true);
  };

  // 内置插件列表
  const builtInColumns = [
    {
      title: t('plugin_name'),
      dataIndex: 'name',
      width: 150,
      render: (name, record) => (
        <Button type="link" onClick={() => openDetailDrawer(record, true)}>
          {name}
        </Button>
      ),
    },
    {
      title: t('plugin_type'),
      dataIndex: 'type',
      width: 100,
      render: (type) => <Tag color="blue">{type || 'custom'}</Tag>,
    },
    {
      title: t('plugin_description'),
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: t('plugin_status'),
      dataIndex: 'status',
      width: 100,
      render: () => <Tag color="green">{t('active')}</Tag>,
    },
    {
      title: t('actions'),
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => openDetailDrawer(record, true)}>
            {t('detail')}
          </Button>
          <Button
            type="link"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => {
              setExecutingPlugin(record);
              setExecuteParams('{}');
              setExecuteResult(null);
              setExecuteModalVisible(true);
            }}
          >
            {t('execute')}
          </Button>
        </Space>
      ),
    },
  ];

  // 自定义插件列表
  const customColumns = [
    {
      title: t('plugin_name'),
      dataIndex: 'name',
      width: 150,
      render: (name, record) => (
        <Button type="link" onClick={() => openDetailDrawer(record, false)}>
          {name}
        </Button>
      ),
    },
    {
      title: t('plugin_display_name'),
      dataIndex: 'display_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('plugin_language'),
      dataIndex: 'language',
      width: 100,
      render: (lang) => {
        const colors = { javascript: 'gold', python: 'green', lua: 'purple' };
        return <Tag color={colors[lang] || 'default'}>{lang?.toUpperCase()}</Tag>;
      },
    },
    {
      title: t('plugin_description'),
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: t('plugin_is_public'),
      dataIndex: 'is_public',
      width: 100,
      render: (isPublic) => (
        <Tag color={isPublic ? 'green' : 'default'}>
          {isPublic ? t('yes') : t('no')}
        </Tag>
      ),
    },
    {
      title: t('plugin_status'),
      dataIndex: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? t('active') : t('inactive')}
        </Tag>
      ),
    },
    {
      title: t('plugin_created_at'),
      dataIndex: 'created_at',
      width: 160,
      render: (date) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: t('actions'),
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditPlugin(record)}>
            {t('edit')}
          </Button>
          <Popconfirm
            title={t('confirm_delete_plugin')}
            description={t('delete_plugin_warning')}
            onConfirm={() => handleDeletePlugin(record.id)}
            okText={t('confirm')}
            cancelText={t('cancel')}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              {t('delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 过滤后的自定义插件
  const filteredCustomPlugins = customPlugins.filter(plugin => {
    const searchLower = searchText.toLowerCase();
    const matchSearch = !searchText ||
      plugin.name?.toLowerCase().includes(searchLower) ||
      plugin.display_name?.toLowerCase().includes(searchLower) ||
      plugin.description?.toLowerCase().includes(searchLower);

    const matchStatus = !statusFilter || plugin.status === statusFilter;
    const matchLanguage = !languageFilter || plugin.language === languageFilter;
    const matchPublic = isPublicFilter === null || plugin.is_public === isPublicFilter;

    return matchSearch && matchStatus && matchLanguage && matchPublic;
  });

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        <RocketOutlined style={{ marginRight: 8 }} />
        {t('plugin_management')}
      </Title>

      <Tabs
        defaultActiveKey="custom"
        size="large"
        items={[
          {
            key: 'built-in',
            label: `${t('built_in_plugins')} (${builtInPlugins.length})`,
            children: (
              <Card>
                <Alert
                  message={t('built_in_plugin_tip')}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Table
                  columns={builtInColumns}
                  dataSource={builtInPlugins}
                  rowKey="name"
                  loading={builtInLoading}
                  pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${t('total')}: ${total}` }}
                  size="small"
                />
              </Card>
            ),
          },
          {
            key: 'custom',
            label: `${t('custom_plugins')} (${customPlugins.length})`,
            children: (
              <Card
                extra={
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleCreatePlugin}>
                    {t('create_plugin')}
                  </Button>
                }
              >
                {/* 搜索筛选栏 */}
                <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Input
                    placeholder={t('search_placeholder')}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 200 }}
                    allowClear
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                  />
                  <Select
                    placeholder={t('plugin_status')}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    allowClear
                    style={{ width: 120 }}
                    options={[
                      { value: 'active', label: t('active') },
                      { value: 'inactive', label: t('inactive') },
                    ]}
                  />
                  <Select
                    placeholder={t('plugin_language')}
                    value={languageFilter}
                    onChange={setLanguageFilter}
                    allowClear
                    style={{ width: 130 }}
                    options={[
                      { value: 'javascript', label: 'JavaScript' },
                      { value: 'python', label: 'Python' },
                      { value: 'lua', label: 'Lua' },
                    ]}
                  />
                  <Select
                    placeholder={t('plugin_is_public')}
                    value={isPublicFilter}
                    onChange={setIsPublicFilter}
                    allowClear
                    style={{ width: 120 }}
                    options={[
                      { value: true, label: t('yes') },
                      { value: false, label: t('no') },
                    ]}
                  />
                  {(searchText || statusFilter || languageFilter || isPublicFilter !== null) && (
                    <Button
                      type="link"
                      onClick={() => {
                        setSearchText('');
                        setStatusFilter(null);
                        setLanguageFilter(null);
                        setIsPublicFilter(null);
                      }}
                    >
                      {t('clear_filters')}
                    </Button>
                  )}
                  <span style={{ marginLeft: 'auto', color: '#8c8c8c', fontSize: 13 }}>
                    {t('total')}: {filteredCustomPlugins.length}
                  </span>
                </div>

                <Table
                  columns={customColumns}
                  dataSource={filteredCustomPlugins}
                  rowKey="id"
                  loading={customLoading}
                  pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${t('total')}: ${total}` }}
                  size="small"
                />
              </Card>
            ),
          },
        ]}
      />

      {/* 创建/编辑插件弹窗 */}
      <Modal
        title={editingPlugin ? t('edit_plugin') : t('create_plugin')}
        open={editModalVisible}
        onOk={handleSavePlugin}
        onCancel={() => setEditModalVisible(false)}
        width={800}
        confirmLoading={saving}
        okText={t('save')}
        cancelText={t('cancel')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                {t('plugin_name')} *
              </label>
              <Input
                value={editFormData.name}
                onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="my-plugin"
                disabled={!!editingPlugin}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                {t('plugin_display_name')}
              </label>
              <Input
                value={editFormData.display_name}
                onChange={(e) => setEditFormData(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder={t('plugin_display_name_placeholder')}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              {t('plugin_description')}
            </label>
            <Input.TextArea
              value={editFormData.description}
              onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('plugin_description_placeholder')}
              rows={2}
            />
          </div>

          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ width: 200 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                {t('plugin_language')} *
              </label>
              <Select
                value={editFormData.language}
                onChange={handleLanguageChange}
                style={{ width: '100%' }}
                disabled={!!editingPlugin}
                options={[
                  { value: 'javascript', label: 'JavaScript' },
                  { value: 'python', label: 'Python' },
                  { value: 'lua', label: 'Lua' },
                ]}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Switch
                checked={editFormData.is_public}
                onChange={(checked) => setEditFormData(prev => ({ ...prev, is_public: checked }))}
              />
              <span style={{ marginLeft: 8 }}>{t('plugin_is_public')}</span>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              {t('plugin_code')} *
            </label>
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              {t('code_template_tip')}
            </Paragraph>
            <TextArea
              value={editFormData.code}
              onChange={(e) => setEditFormData(prev => ({ ...prev, code: e.target.value }))}
              placeholder={t('code_placeholder')}
              rows={15}
              style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: 12 }}
            />
          </div>
        </div>
      </Modal>

      {/* 插件详情抽屉 */}
      <Drawer
        title={detailPlugin?.name || t('plugin_detail')}
        placement="right"
        width={600}
        open={detailDrawerVisible}
        onClose={() => setDetailDrawerVisible(false)}
      >
        {detailPlugin && (
          <div>
            <Descriptions column={2} variant="bordered" size="small">
              <Descriptions.Item label={t('plugin_name')}>{detailPlugin.name}</Descriptions.Item>
              <Descriptions.Item label={t('plugin_type')}>
                <Tag color={detailPlugin.is_built_in ? 'blue' : 'orange'}>
                  {detailPlugin.is_built_in ? t('built_in') : t('custom')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('plugin_display_name')} span={2}>
                {detailPlugin.display_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('plugin_language')}>
                {detailPlugin.language?.toUpperCase() || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('plugin_is_public')}>
                {detailPlugin.is_public ? t('yes') : t('no')}
              </Descriptions.Item>
              <Descriptions.Item label={t('plugin_status')}>
                <Tag color={detailPlugin.status === 'active' ? 'green' : 'red'}>
                  {detailPlugin.status === 'active' ? t('active') : t('inactive')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('plugin_created_at')} span={2}>
                {detailPlugin.created_at ? new Date(detailPlugin.created_at).toLocaleString() : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('plugin_description')} span={2}>
                {detailPlugin.description || '-'}
              </Descriptions.Item>
            </Descriptions>

            {detailPlugin.code && (
              <>
                <Divider>{t('plugin_code')}</Divider>
                <pre style={{
                  background: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  maxHeight: 400,
                  overflow: 'auto',
                  fontSize: '12px'
                }}>
                  <code>{detailPlugin.code}</code>
                </pre>
              </>
            )}
          </div>
        )}
      </Drawer>

      {/* 执行插件弹窗 */}
      <Modal
        title={`${t('execute_plugin')}: ${executingPlugin?.name || ''}`}
        open={executeModalVisible}
        onCancel={() => setExecuteModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setExecuteModalVisible(false)}>
            {t('close')}
          </Button>,
          <Button key="execute" type="primary" loading={executing} onClick={handleExecutePlugin}>
            {t('execute')}
          </Button>,
        ]}
      >
        <div>
          <Paragraph>
            <Text strong>{t('execution_params')}:</Text>
          </Paragraph>
          <TextArea
            value={executeParams}
            onChange={(e) => setExecuteParams(e.target.value)}
            rows={6}
            placeholder='{"key": "value"}'
            style={{ fontFamily: 'Monaco, Consolas, monospace', marginBottom: 16 }}
          />

          {executeResult && (
            <>
              <Paragraph>
                <Text strong>{t('execution_result')}:</Text>
              </Paragraph>
              <pre style={{
                background: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
                maxHeight: 300,
                overflow: 'auto',
                fontSize: '12px'
              }}>
                <code>{JSON.stringify(executeResult, null, 2)}</code>
              </pre>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Plugins;
